import type {
  ContextualTip,
  TipRegistry,
  TipTour,
  TipWhenContext,
} from 'productTips';

const FIRST_WORKSPACE: TipTour = {
  id: 'screening_first_workspace',
  title: 'Screening home',
  description: 'Orient on Jobs and Settings.',
  version: 1,
  autoStart: true,
  routeMatch: ['/screening'],
  steps: [
    {
      id: 'scr_logo',
      target: 'shell-logo',
      eyebrow: 'Screening',
      title: 'HireMe Screening',
      body: 'This product is for posting roles and reviewing applicants. The logo returns you to Roles.',
      placement: 'bottom',
    },
    {
      id: 'scr_nav',
      target: 'shell-nav',
      eyebrow: 'Screening',
      title: 'Roles and Settings',
      body: 'Roles is your hiring home. Settings holds company name and careers URL used on public boards.',
      placement: 'bottom',
    },
    {
      id: 'scr_post',
      target: 'screening-post-job',
      eyebrow: 'Screening',
      title: 'Post a role',
      body: 'Create a role with structured specs. Applicants interview against that definition, so every review starts from the same bar.',
      placement: 'bottom',
    },
    {
      id: 'scr_list',
      target: 'screening-job-list',
      eyebrow: 'Screening',
      title: 'Your roles',
      body: 'Open a role to manage applicants, integrate the apply link, and review interview analysis.',
      placement: 'top',
    },
    {
      id: 'scr_account',
      target: 'shell-account',
      eyebrow: 'Screening',
      title: 'Account',
      body: 'Company identity and sign-out live here. Tips stay available from the toolbar whenever you need them.',
      placement: 'bottom',
    },
    {
      id: 'scr_tips',
      target: 'tips-trigger',
      eyebrow: 'Screening',
      title: 'Tips',
      body: 'Replay walkthroughs or read short guidance for the screen you are on — without leaving the page.',
      placement: 'bottom',
    },
  ],
};

const JOB_LIFECYCLE: TipTour = {
  id: 'screening_job_lifecycle',
  title: 'Job lifecycle',
  description: 'From role detail to applicants and interview review.',
  version: 1,
  autoStart: true,
  routeMatch: ['/screening/jobs/'],
  steps: [
    {
      id: 'job_header',
      target: 'page-header',
      eyebrow: 'Role',
      title: 'The role record',
      body: 'Everything for this opening hangs off this role: applicants, the integrate link, and settings.',
      placement: 'bottom',
    },
    {
      id: 'job_segments',
      target: 'job-segments',
      eyebrow: 'Role',
      title: 'Filters',
      body: 'Switch between every applicant and Melon’s recommendations, and choose whether invites wait for your approval.',
      placement: 'bottom',
    },
    {
      id: 'job_integrate',
      target: 'job-integrate',
      eyebrow: 'Role',
      title: 'Integrate',
      body: 'Copy the hosted board or embed snippet onto your careers page so candidates can apply in place.',
      placement: 'bottom',
    },
    {
      id: 'job_applicants',
      target: 'job-applicants',
      eyebrow: 'Role',
      title: 'Applicants',
      body: 'Each row is a candidate. Open one to review interview analysis and make a decision.',
      placement: 'top',
    },
  ],
};

const SETTINGS_TOUR: TipTour = {
  id: 'screening_settings',
  title: 'Settings',
  description: 'Company name and careers URL.',
  version: 1,
  autoStart: true,
  routeMatch: ['/screening/settings'],
  steps: [
    {
      id: 'settings_form',
      target: 'screening-settings-form',
      eyebrow: 'Settings',
      title: 'Public identity',
      body: 'Company name and careers page URL appear on boards candidates see. Keep them accurate before you share links.',
      placement: 'bottom',
    },
  ],
};

const TIPS: ContextualTip[] = [
  {
    id: 'scr_tip_first_role',
    section: 'Roles',
    title: 'Post before you polish',
    body: 'A clear role with solid specs beats a perfect careers page. You can refine copy after the first applicants appear.',
    version: 1,
  },
  {
    id: 'scr_tip_integrate',
    section: 'Integrate',
    title: 'One link, many surfaces',
    body: 'Use the hosted board URL or embed snippet. Candidates should never need a HireMe login to apply.',
    version: 1,
  },
  {
    id: 'scr_tip_review',
    section: 'Applicants',
    title: 'Decide from the interview',
    body: 'Open the interview review before shortlisting. The transcript and analysis are the evidence — not the CV alone.',
    version: 1,
  },
];

export const screeningTipRegistry: TipRegistry = {
  tours: [FIRST_WORKSPACE, JOB_LIFECYCLE, SETTINGS_TOUR],
  tips: TIPS,
};

export const resolveScreeningTips = (ctx: TipWhenContext) => {
  const path = ctx.asPath.split('?')[0];
  const tours: TipTour[] = [];
  let autoTourId: string | null = null;

  const onJobsHome = path === '/screening' || path === '/screening/';
  const onSettings = path.startsWith('/screening/settings');
  const onJobDetail =
    path.startsWith('/screening/jobs/') &&
    !path.endsWith('/new') &&
    !path.includes('/edit');

  if (onJobsHome) {
    // Soft-skip empty-state competition: still offer tour, but if no jobs yet
    // drop the job-list step so Post a job owns the moment.
    let home = FIRST_WORKSPACE;
    if (ctx.screening?.hasJobs === false) {
      home = {
        ...FIRST_WORKSPACE,
        steps: FIRST_WORKSPACE.steps.filter(
          (s) => s.target !== 'screening-job-list'
        ),
      };
    }
    tours.push(home);
    autoTourId = home.id;
  }

  if (onJobDetail) {
    tours.push(JOB_LIFECYCLE);
    if (!autoTourId) autoTourId = JOB_LIFECYCLE.id;
  }

  if (onSettings) {
    tours.push(SETTINGS_TOUR);
    if (!autoTourId) autoTourId = SETTINGS_TOUR.id;
  }

  // Panel always offers the other tours without auto-start stacking.
  for (const tour of screeningTipRegistry.tours) {
    if (!tours.some((t) => t.id === tour.id)) {
      tours.push({ ...tour, autoStart: false });
    }
  }

  if (ctx.screening?.isOnboarding || ctx.screening?.billingReady === false) {
    return {
      tours,
      tips: screeningTipRegistry.tips,
      autoTourId: null,
    };
  }

  return {
    tours,
    tips: screeningTipRegistry.tips,
    autoTourId,
  };
};
